import { Fragment } from 'react';
import { money } from '../../utils/format';

export interface DayBookEntry {
  id: string;
  journalNo: string;
  journalType: string;
  narration: string | null;
  sourceDocType: string;
  branchName: string | null;
  party: string | null;
  postedBy: string | null;
  reversed: boolean;
  isReversal: boolean;
  value: string;
  cashIn: string;
  cashOut: string;
  bankIn: string;
  bankOut: string;
}

export interface Balance {
  cash: string;
  bank: string;
}

export interface DayBookDay {
  date: string;
  opening: Balance;
  received: Balance;
  paid: Balance;
  closing: Balance;
  accounts: { code: string; name: string; type: 'CASH' | 'BANK'; opening: string; closing: string }[];
  entries: DayBookEntry[];
  value: string;
}

export interface DayBook {
  from: string;
  to: string;
  accounts: { id: string; code: string; name: string; type: 'CASH' | 'BANK' }[];
  opening: Balance;
  received: Balance;
  paid: Balance;
  closing: Balance;
  entryCount: number;
  value: string;
  days: DayBookDay[];
}

const n = (v: string | null | undefined): number => Number(v || 0);
const both = (b: Balance): number => n(b.cash) + n(b.bank);

/** A figure, or a quiet dash where nothing moved — a column of ₹0.00 hides the ones that did. */
const amt = (v: string): string => (n(v) === 0 ? '' : money(v));

/** A day's total: the figure, or a dash when nothing moved that way. */
const total = (v: string): string => (n(v) === 0 ? '—' : money(v));

const longDay = (d: string): string =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const shortDay = (d: string): string =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const SOURCE: Record<string, string> = {
  SALE: 'Sale',
  SALES_RETURN: 'Customer return',
  PURCHASE: 'Purchase',
  PURCHASE_RETURN: 'Return to supplier',
  OLD_GOLD: 'Old gold bought',
  EXCHANGE: 'Exchange',
  RECEIPT: 'Money received',
  PAYMENT: 'Payment made',
  EXPENSE: 'Expense',
  OTHER_INCOME: 'Other income',
  CASH_CLOSING: 'Till count',
  CHIT_RECEIPT: 'Savings instalment',
  CHIT_REDEMPTION: 'Savings redeemed',
  MELTING: 'Melting',
  JOB_WORK: 'Job work',
  STOCK_ADJUSTMENT: 'Stock adjustment',
  STOCK_TRANSFER: 'Branch transfer',
  STOCK_ITEM: 'Opening stock',
  MANUAL_JOURNAL: 'Manual entry',
  MANUAL: 'Manual entry',
};
const sourceWord = (s: string): string => SOURCE[s] ?? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

/** The opening or closing line of a period or a day: cash, bank, and the two together. */
function BalanceLine({ label, b, strong }: { label: string; b: Balance; strong?: boolean }) {
  return (
    <div className={`flex flex-wrap items-baseline gap-x-5 gap-y-0.5 ${strong ? 'font-semibold' : ''}`}>
      <span className="min-w-[7rem] text-ink-soft">{label}</span>
      <span>
        <span className="text-2xs uppercase tracking-wide text-ink-soft">Cash </span>
        <span className={`num ${n(b.cash) < 0 ? 'text-danger' : ''}`}>{money(b.cash)}</span>
      </span>
      <span>
        <span className="text-2xs uppercase tracking-wide text-ink-soft">Bank </span>
        <span className={`num ${n(b.bank) < 0 ? 'text-danger' : ''}`}>{money(b.bank)}</span>
      </span>
      <span>
        <span className="text-2xs uppercase tracking-wide text-ink-soft">Total </span>
        <span className="num">{money(both(b).toFixed(2))}</span>
      </span>
    </div>
  );
}

/** The period at a glance: what it opened with, what came in and went out, what it closed on. */
export function DayBookSummary({ book }: { book: DayBook }) {
  const cols: { label: string; sub: string; cash: string; bank: string; tone?: string }[] = [
    { label: 'Opening', sub: `on ${shortDay(book.from)}`, cash: book.opening.cash, bank: book.opening.bank },
    { label: 'Received', sub: 'money in', cash: book.received.cash, bank: book.received.bank, tone: 'text-success' },
    { label: 'Paid out', sub: 'money out', cash: book.paid.cash, bank: book.paid.bank, tone: 'text-danger' },
    { label: 'Closing', sub: `on ${shortDay(book.to)}`, cash: book.closing.cash, bank: book.closing.bank },
  ];
  return (
    <section className="keep-together mb-5 overflow-hidden rounded-xl border border-silver-100 bg-surface shadow-card">
      <div className="grid grid-cols-2 divide-x divide-silver-100 md:grid-cols-4">
        {cols.map((c, i) => (
          <div
            key={c.label}
            className={`px-4 py-3 ${i === 3 ? 'bg-velvet text-white print:bg-transparent print:text-ink' : ''}`}
          >
            <p
              className={`text-2xs uppercase tracking-wide ${i === 3 ? 'text-gold-200 print:text-ink-soft' : 'text-ink-soft'}`}
            >
              {c.label} <span className="normal-case tracking-normal">· {c.sub}</span>
            </p>
            <p className={`num mt-1 text-xl font-semibold ${i === 3 ? '' : (c.tone ?? '')}`}>
              {i === 1 ? '+' : i === 2 ? '−' : ''}
              {money((n(c.cash) + n(c.bank)).toFixed(2))}
            </p>
            <p className={`num mt-0.5 text-2xs ${i === 3 ? 'text-white/80 print:text-ink-soft' : 'text-ink-soft'}`}>
              Cash {money(c.cash)} · Bank {money(c.bank)}
            </p>
          </div>
        ))}
      </div>
      <p className="border-t border-silver-100 px-4 py-1.5 text-2xs text-ink-soft">
        {book.days.length} day{book.days.length === 1 ? '' : 's'} with entries · {book.entryCount} entries · posted
        value <span className="num">{money(book.value)}</span>. Opening + received − paid = closing, and each day closes
        on the next day’s opening.
      </p>
    </section>
  );
}

/**
 * One day of the book.
 *
 * It opens on the cash and bank the day before closed on, lists every entry
 * with the money it moved, and closes — the closing carried to the next day.
 * An entry that moved no money (a credit sale, stock, a melt) still shows its
 * value, so the day is complete.
 */
export function DayBookDayCard({
  day,
  nextDate,
  shown,
  onOpen,
}: {
  day: DayBookDay;
  nextDate: string | null;
  /** The entries to list — all of them, or only one kind. Balances always count every entry. */
  shown: DayBookEntry[];
  onOpen: (id: string) => void;
}) {
  const nonCash = shown.filter((e) => n(e.cashIn) + n(e.cashOut) + n(e.bankIn) + n(e.bankOut) === 0);
  return (
    <section className="mb-5 overflow-hidden rounded-xl border border-silver-100 bg-surface shadow-card print:mb-4 print:overflow-visible print:rounded-none print:border-ink/30 print:shadow-none">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-silver-100 bg-gold-50/60 px-4 py-2.5 print:border-ink/30">
        <h3 className="text-base font-semibold">{longDay(day.date)}</h3>
        <span className="text-2xs text-ink-soft">
          {day.entries.length} entr{day.entries.length === 1 ? 'y' : 'ies'} · value{' '}
          <span className="num">{money(day.value)}</span>
        </span>
      </div>

      <div className="border-b border-silver-100 px-4 py-2 text-sm">
        <BalanceLine label="Opening balance" b={day.opening} />
      </div>

      <div className="overflow-x-auto print:overflow-visible">
        <table className="w-full min-w-[860px] table-fixed border-collapse text-sm print:min-w-0 print:text-[10px]">
          {/* The same widths every day, so the money columns line up down the page. */}
          <colgroup>
            <col className="w-32 print:w-[4.5rem]" />
            <col />
            <col className="w-36 print:w-[5.4rem]" />
            <col className="w-36 print:w-[5.4rem]" />
            <col className="w-36 print:w-[5.4rem]" />
            <col className="w-36 print:w-[5.4rem]" />
            <col className="w-36 print:w-[5.4rem]" />
          </colgroup>
          <thead>
            <tr className="bg-silver-50 text-2xs text-ink-soft">
              <th className="px-4 py-1.5 text-left font-semibold" rowSpan={2}>
                Voucher
              </th>
              <th className="px-2 py-1.5 text-left font-semibold" rowSpan={2}>
                Particulars
              </th>
              <th className="border-l border-silver-100 px-2 pt-1.5 text-center font-semibold" colSpan={2}>
                Cash
              </th>
              <th className="border-l border-silver-100 px-2 pt-1.5 text-center font-semibold" colSpan={2}>
                Bank
              </th>
              <th className="border-l border-silver-100 px-4 py-1.5 text-right font-semibold" rowSpan={2}>
                No money moved
              </th>
            </tr>
            <tr className="bg-silver-50 text-2xs text-ink-soft">
              <th className="border-l border-silver-100 px-2 pb-1.5 text-right font-medium">Received</th>
              <th className="px-2 pb-1.5 text-right font-medium">Paid</th>
              <th className="border-l border-silver-100 px-2 pb-1.5 text-right font-medium">Received</th>
              <th className="px-2 pb-1.5 text-right font-medium">Paid</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((e) => {
              const moved = n(e.cashIn) + n(e.cashOut) + n(e.bankIn) + n(e.bankOut) > 0;
              return (
                <tr
                  key={e.id}
                  className="cursor-pointer border-t border-silver-100/70 align-top hover:bg-gold-50"
                  onClick={() => onOpen(e.id)}
                >
                  <td className="whitespace-nowrap px-4 py-2">
                    <span className="num text-gold-700 underline-offset-2 hover:underline">{e.journalNo}</span>
                    {e.reversed ? <span className="block text-2xs text-danger">reversed</span> : null}
                    {e.isReversal ? <span className="block text-2xs text-ink-soft">reversal</span> : null}
                  </td>
                  <td className="px-2 py-2">
                    <span className="block">{e.narration ?? sourceWord(e.sourceDocType)}</span>
                    <span className="block text-2xs text-ink-soft">
                      {[sourceWord(e.sourceDocType), e.party, e.branchName, e.postedBy ? `by ${e.postedBy}` : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </td>
                  <td className="num whitespace-nowrap border-l border-silver-100 px-2 py-2 text-right text-success">
                    {amt(e.cashIn)}
                  </td>
                  <td className="num whitespace-nowrap px-2 py-2 text-right text-danger">{amt(e.cashOut)}</td>
                  <td className="num whitespace-nowrap border-l border-silver-100 px-2 py-2 text-right text-success">
                    {amt(e.bankIn)}
                  </td>
                  <td className="num whitespace-nowrap px-2 py-2 text-right text-danger">{amt(e.bankOut)}</td>
                  <td className="num whitespace-nowrap border-l border-silver-100 px-4 py-2 text-right text-ink-soft">
                    {moved ? '' : money(e.value)}
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-center text-sm text-ink-soft">
                  No entries of this kind on this day.
                </td>
              </tr>
            ) : null}
          </tbody>
          <tfoot>
            <tr className="border-t border-ink/20 bg-silver-50 font-semibold">
              <td className="px-4 py-2" colSpan={2}>
                Day’s total
              </td>
              <td className="num whitespace-nowrap border-l border-silver-100 px-2 py-2 text-right">
                {total(day.received.cash)}
              </td>
              <td className="num whitespace-nowrap px-2 py-2 text-right">{total(day.paid.cash)}</td>
              <td className="num whitespace-nowrap border-l border-silver-100 px-2 py-2 text-right">
                {total(day.received.bank)}
              </td>
              <td className="num whitespace-nowrap px-2 py-2 text-right">{total(day.paid.bank)}</td>
              <td className="num whitespace-nowrap border-l border-silver-100 px-4 py-2 text-right text-ink-soft">
                {nonCash.length > 0 ? money(nonCash.reduce((a, e) => a + n(e.value), 0).toFixed(2)) : ''}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 border-t-2 border-velvet/70 bg-velvet-50 px-4 py-2.5 text-sm print:bg-transparent">
        <BalanceLine label="Closing balance" b={day.closing} strong />
        <span className="text-2xs text-ink-soft">
          {nextDate ? `Carried to ${shortDay(nextDate)} as its opening` : 'Closing of the period'}
        </span>
      </div>
      {day.accounts.length > 2 ? (
        <div className="flex flex-wrap gap-x-5 gap-y-0.5 border-t border-silver-100 px-4 py-1.5 text-2xs text-ink-soft">
          {day.accounts.map((a) => (
            <Fragment key={a.code}>
              <span>
                {a.name}: <span className="num">{money(a.opening)}</span> →{' '}
                <span className={`num font-medium ${n(a.closing) < 0 ? 'text-danger' : 'text-ink'}`}>
                  {money(a.closing)}
                </span>
              </span>
            </Fragment>
          ))}
        </div>
      ) : null}
    </section>
  );
}
